"use client";

import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";

export default function CampaignApplicationDetailsModal({
  modal_center,
  setmodal_center,
  tog_center,
  application,
  onStatusUpdate,
}) {
  const [isUpdating, setIsUpdating] = useState(false);

  // Helper functions to safely render values
  const safeString = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "object") return "N/A";
    return String(value);
  };

  const safeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "object") return 0;
    return Number(value) || 0;
  };

  const updateApplicationStatus = async (newStatus) => {
    if (!application || !application.id) return;

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/campaign-applications/${application.id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(`Application ${newStatus} successfully`, {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: "colored",
        });

        // Update the application in parent component
        if (onStatusUpdate) {
          onStatusUpdate(application.id, newStatus);
        }

        // Close modal
        tog_center();
      } else {
        toast.error(
          response.data.message || "Failed to update application status",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error updating application status:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "warning", text: "Pending" },
      approved: { color: "success", text: "Approved" },
      rejected: { color: "danger", text: "Rejected" },
      completed: { color: "primary", text: "Completed" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      text: "Unknown",
    };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(safeNumber(price));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!application) return null;

  const isPending = application.status === "pending";

  return (
    <Modal
      isOpen={modal_center}
      toggle={tog_center}
      centered
      size="lg"
      className="application-details-modal"
    >
      <ModalHeader toggle={tog_center}>
        <div className="d-flex align-items-center">
          <i className="ri-file-user-line me-2 text-primary"></i>
          Campaign Application Details
          {isPending && (
            <Badge color="warning" className="ms-2">
              <i className="ri-time-line me-1"></i>
              Needs Review
            </Badge>
          )}
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="application-details">
          {/* Application Header */}
          <Row className="mb-4">
            <Col md={8}>
              <div className="d-flex align-items-start">
                {application.influencer?.profile_image && (
                  <img
                    src={
                      "https://api.tafaseelapp.com/storage/" +
                        application.influencer.profile_image ||
                      "/placeholder.svg"
                    }
                    alt={safeString(application.influencer?.name)}
                    className="rounded-circle me-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                    }}
                  />
                )}
                <div>
                  <h5 className="mb-2">
                    {safeString(application.influencer?.name)}
                  </h5>
                  <p className="text-muted mb-2">
                    {safeString(application.influencer?.email)}
                  </p>
                  <div className="d-flex gap-2 align-items-center">
                    {getStatusBadge(application.status)}
                    <Badge color="light" className="text-dark">
                      Application #{safeString(application.id)}
                    </Badge>
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <div className="application-date">
                <h6 className="text-primary mb-0">Applied On</h6>
                <small className="text-muted">
                  {formatDate(application.applied_at || application.created_at)}
                </small>
              </div>
            </Col>
          </Row>

          {/* Campaign Information */}
          <Row className="mb-4">
            <Col>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-megaphone-line me-2"></i>
                  Campaign Information
                </h6>
                <div className="campaign-info p-3 bg-light rounded">
                  <Row>
                    <Col md={8}>
                      <h6 className="mb-2">
                        {safeString(application.campaign?.title)}
                      </h6>
                      <p className="text-muted mb-2">
                        {safeString(application.campaign?.description)}
                      </p>
                      <div className="d-flex gap-2 mb-2">
                        <Badge color="info">
                          {safeString(application.campaign?.category?.name)}
                        </Badge>
                        <Badge color="secondary">
                          {safeString(application.campaign?.location)}
                        </Badge>
                      </div>
                    </Col>
                    <Col md={4} className="text-end">
                      <h5 className="text-success mb-0">
                        {formatPrice(application.campaign?.price)}
                      </h5>
                      <small className="text-muted">Campaign Budget</small>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>

          {/* Application Details */}
          <Row className="mb-4">
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-information-line me-2"></i>
                  Application Information
                </h6>
                <div className="info-item mb-2">
                  <strong>Application ID:</strong> #{safeString(application.id)}
                </div>
                <div className="info-item mb-2">
                  <strong>Status:</strong> {getStatusBadge(application.status)}
                </div>
                <div className="info-item mb-2">
                  <strong>Applied Date:</strong>{" "}
                  {formatDateTime(
                    application.applied_at || application.created_at
                  )}
                </div>
                {application.reviewed_at && (
                  <div className="info-item mb-2">
                    <strong>Reviewed Date:</strong>{" "}
                    {formatDateTime(application.reviewed_at)}
                  </div>
                )}
                <div className="info-item mb-2">
                  <strong>Last Updated:</strong>{" "}
                  {formatDateTime(application.updated_at)}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-user-star-line me-2"></i>
                  Influencer Information
                </h6>
                <div className="info-item mb-2">
                  <strong>Name:</strong>{" "}
                  {safeString(application.influencer?.name)}
                </div>
                <div className="info-item mb-2">
                  <strong>Email:</strong>{" "}
                  {safeString(application.influencer?.email)}
                </div>
                {application.influencer?.bio && (
                  <div className="info-item mb-2">
                    <strong>Bio:</strong>{" "}
                    {safeString(application.influencer.bio)}
                  </div>
                )}
                <div className="info-item mb-2">
                  <strong>Followers:</strong>{" "}
                  {safeNumber(
                    application.influencer?.followers_count
                  ).toLocaleString()}
                </div>
                <div className="info-item mb-2">
                  <strong>Engagement Rate:</strong>{" "}
                  {safeNumber(application.influencer?.engagement_rate)}%
                </div>
              </div>
            </Col>
          </Row>

          {/* Application Message */}
          {application.message && (
            <Row className="mb-4">
              <Col>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-message-line me-2"></i>
                    Application Message
                  </h6>
                  <div className="message-content p-3 bg-light rounded">
                    <p className="mb-0">{safeString(application.message)}</p>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Business Notes */}
          {application.business_notes && (
            <Row className="mb-4">
              <Col>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-sticky-note-line me-2"></i>
                    Business Notes
                  </h6>
                  <div className="notes-content p-3 bg-warning bg-opacity-10 rounded">
                    <p className="mb-0">
                      {safeString(application.business_notes)}
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Business Information */}
          <Row className="mb-4">
            <Col>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-building-line me-2"></i>
                  Business Information
                </h6>
                <Row>
                  <Col md={6}>
                    <div className="info-item mb-2">
                      <strong>Business Name:</strong>{" "}
                      {safeString(application.campaign?.user?.name)}
                    </div>
                    <div className="info-item mb-2">
                      <strong>Email:</strong>{" "}
                      {safeString(application.campaign?.user?.email)}
                    </div>
                  </Col>
                  <Col md={6}>
                    {application.campaign?.user?.phone && (
                      <div className="info-item mb-2">
                        <strong>Phone:</strong>{" "}
                        {safeString(application.campaign.user.phone)}
                      </div>
                    )}
                    <div className="info-item mb-2">
                      <strong>Member Since:</strong>{" "}
                      {formatDate(application.campaign?.user?.created_at)}
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          {/* Campaign Rules */}
          {application.campaign?.rules && (
            <Row className="mb-4">
              <Col>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-file-list-line me-2"></i>
                    Campaign Rules & Requirements
                  </h6>
                  <div className="rules-content p-3 bg-info bg-opacity-10 rounded">
                    <p className="mb-0">
                      {safeString(application.campaign.rules)}
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Influencer Categories */}
          {application.influencer?.categories &&
            application.influencer.categories.length > 0 && (
              <Row>
                <Col>
                  <div className="info-section">
                    <h6 className="text-primary mb-3">
                      <i className="ri-price-tag-line me-2"></i>
                      Influencer Categories
                    </h6>
                    <div className="categories-list">
                      {application.influencer.categories.map(
                        (category, index) => (
                          <Badge
                            key={index}
                            color="outline-primary"
                            className="me-2 mb-2"
                          >
                            {safeString(category)}
                          </Badge>
                        )
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="d-flex justify-content-between w-100">
          {/* Status Actions */}
          {isPending ? (
            <div className="approval-actions">
              <h6 className="mb-2 text-warning">
                <i className="ri-time-line me-2"></i>
                Application Review Required:
              </h6>
              <div className="d-flex gap-2">
                <Button
                  color="success"
                  onClick={() => updateApplicationStatus("approved")}
                  disabled={isUpdating}
                >
                  <i className="ri-check-line me-1"></i>
                  {isUpdating ? "Approving..." : "Approve Application"}
                </Button>
                <Button
                  color="danger"
                  onClick={() => updateApplicationStatus("rejected")}
                  disabled={isUpdating}
                >
                  <i className="ri-close-line me-1"></i>
                  {isUpdating ? "Rejecting..." : "Reject Application"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="status-actions">
              <h6 className="mb-2">Update Status:</h6>
              <div className="d-flex gap-2 flex-wrap">
                {application.status !== "approved" && (
                  <Button
                    color="success"
                    size="sm"
                    onClick={() => updateApplicationStatus("approved")}
                    disabled={isUpdating}
                  >
                    <i className="ri-check-line me-1"></i>
                    Approve
                  </Button>
                )}
                {application.status !== "rejected" && (
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => updateApplicationStatus("rejected")}
                    disabled={isUpdating}
                  >
                    <i className="ri-close-line me-1"></i>
                    Reject
                  </Button>
                )}
                {application.status !== "completed" &&
                  application.status === "approved" && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => updateApplicationStatus("completed")}
                      disabled={isUpdating}
                    >
                      <i className="ri-check-double-line me-1"></i>
                      Mark Complete
                    </Button>
                  )}
                {application.status !== "pending" && (
                  <Button
                    color="warning"
                    size="sm"
                    onClick={() => updateApplicationStatus("pending")}
                    disabled={isUpdating}
                  >
                    <i className="ri-time-line me-1"></i>
                    Reset to Pending
                  </Button>
                )}
              </div>
            </div>
          )}
          <div>
            <Button color="secondary" onClick={tog_center}>
              Close
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
}
